import { ShareTo } from "../../config/Enums";
import { IShareSocialMediaItem } from "../../models/Interfaces";
import PostFormatter from "../socialmedia/PostFormatter";
import { ISocialPublishResult, ISocialPublishingProvider } from "./types";

class FacebookPublishingProvider implements ISocialPublishingProvider {
  public readonly platform = ShareTo.Facebook;
  public static readonly storageKey = "PublishingFacebookToken";

  public isEnabled(): boolean {
    return !!import.meta.env.VITE_FACEBOOK_API_ID;
  }

  public isConnected(): boolean {
    return !!window.localStorage.getItem(FacebookPublishingProvider.storageKey);
  }

  private async ensureInitialized(): Promise<void> {
    if (!window.FB) {
      await this.initializeSDK();
    }
  }

  private initializeSDK(): Promise<void> {
    const appId: string | undefined = import.meta.env.VITE_FACEBOOK_API_ID;
    const fbUrl: string = import.meta.env.VITE_FACEBOOK_SDK_URL || "https://connect.facebook.net/en_US/sdk.js";
    const apiVer: string = import.meta.env.VITE_FACEBOOK_API_VERSION || "v15.0";

    return new Promise((resolve, reject) => {
      if (window.FB) {
        resolve();
        return;
      }

      if (!appId) {
        reject(new Error("Missing Facebook parameters from environment"));
        return;
      }

      const script = document.createElement("script");
      script.src = fbUrl;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        try {
          window.fbAsyncInit = () => {
            window.FB.init({
              appId,
              cookie: true,
              xfbml: true,
              version: apiVer
            });
            resolve();
          };
        } catch {
          reject(new Error("Failed to initialize Facebook SDK"));
        }
      };
      script.onerror = () => reject(new Error("Failed to load Facebook SDK"));
      document.head.appendChild(script);
    });
  }

  private saveToken(token: string): void {
    window.localStorage.setItem(FacebookPublishingProvider.storageKey, token);
  }

  private getToken(): string | null {
    return window.localStorage.getItem(FacebookPublishingProvider.storageKey);
  }

  private removeToken(): void {
    window.localStorage.removeItem(FacebookPublishingProvider.storageKey);
  }

  public async connect(): Promise<void> {
    const configuredScopes: string = import.meta.env.VITE_FACEBOOK_SCOPES ?? "public_profile,email,pages_show_list,pages_manage_posts";
    const scopeList = configuredScopes
      .split(",")
      .map(scope => scope.trim())
      .filter(scope => scope.length > 0);

    if (!scopeList.includes("public_profile")) {
      scopeList.push("public_profile");
    }

    if (!scopeList.includes("email")) {
      scopeList.push("email");
    }

    if (!scopeList.includes("pages_show_list")) {
      scopeList.push("pages_show_list");
    }

    if (!scopeList.includes("pages_manage_posts")) {
      scopeList.push("pages_manage_posts");
    }

    await this.ensureInitialized();

    await new Promise<void>((resolve, reject) => {
      window.FB.login((response: fb.StatusResponse) => {
        if (response.status === "connected" && response.authResponse?.accessToken) {
          this.saveToken(response.authResponse.accessToken);
          resolve();
        } else {
          reject(new Error("Facebook publishing login failed."));
        }
      }, { scope: scopeList.join(",") });
    });
  }

  public async disconnect(): Promise<void> {
    await this.ensureInitialized();
    await new Promise<void>((resolve) => {
      window.FB.logout(() => {
        this.removeToken();
        resolve();
      });
    });
  }

  private async ensurePublishingLogin(): Promise<boolean> {
    await this.ensureInitialized();

    return new Promise((resolve) => {
      window.FB.getLoginStatus((response: fb.StatusResponse) => {
        if (response.status === "connected" && response.authResponse?.accessToken) {
          this.saveToken(response.authResponse.accessToken);
          resolve(true);
          return;
        }

        this.connect()
          .then(() => resolve(true))
          .catch(() => {
            this.removeToken();
            resolve(false);
          });
      });
    });
  }

  private async getPages(): Promise<Array<{ id: string; name: string; accessToken: string }>> {
    await this.ensureInitialized();

    const token = this.getToken();
    if (!token) {
      throw new Error("Facebook publishing token was missing.");
    }

    return new Promise((resolve, reject) => {
      window.FB.api("/me/accounts", "get", { access_token: token }, (response: any) => {
        if (response && !response.error) {
          const pages = (response.data ?? []).map((page: any) => ({
            id: page.id,
            name: page.name,
            accessToken: page.access_token
          }));
          resolve(pages);
        } else {
          reject(new Error(response?.error?.message || "Error fetching Facebook pages"));
        }
      });
    });
  }

  private async postToPage(pageId: string, message: string, link?: string, imageUrl?: string): Promise<any> {
    await this.ensureInitialized();

    const pages = await this.getPages();
    const page = pages.find((p) => p.id === pageId);

    if (!page?.accessToken) {
      throw new Error("Facebook page access token was missing.");
    }

    const hasPhoto = !!imageUrl;
    const postData: Record<string, string> = hasPhoto
      ? { url: imageUrl ?? "", caption: `${message}${link ? `\n\nRead More: ${link}` : ""}` }
      : { message };

    if (link) {
      postData.link = link;
    }

    const endpoint = hasPhoto ? `/${pageId}/photos` : `/${pageId}/feed`;

    return new Promise((resolve, reject) => {
      window.FB.api(endpoint, "post", { ...postData, access_token: page.accessToken }, (response: any) => {
        if (response && !response.error) {
          resolve(response);
        } else {
          reject(new Error(response?.error?.message || "Error posting to Facebook page"));
        }
      });
    });
  }

  public async sharePost(sharedItem: IShareSocialMediaItem): Promise<ISocialPublishResult> {
    const isLoggedIn = await this.ensurePublishingLogin();
    if (!isLoggedIn) {
      return { status: "pending" };
    }

    const pages = await this.getPages();
    if (pages.length < 1) {
      return { status: "failed" };
    }

    const formattedPost = await PostFormatter.html2Unicode(sharedItem.PostText);
    const postShared = await this.postToPage(
      pages[0].id,
      formattedPost,
      sharedItem.IncludePageLink ? sharedItem.VersionUrl : undefined,
      sharedItem.ImageUrl
    );

    return { status: postShared !== null ? "success" : "failed" };
  }
}

export default FacebookPublishingProvider;
